import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiUrl } from '@/config/api';
import { fetchWithIdempotencyRetry } from '@/utils/idempotency';
import { isTextWithinLimit, TEXT_LIMITS } from '@/utils/textLimits.js';

const MAX_FILES = 3;
const MAX_FILE_SIZE_BYTES = 3 * 1024 * 1024;
const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const MAX_CONDUCTOR_MESSAGES_IN_ROW = 3;
const SUPPORT_PREVIEW_OVERLAY_CLASS = 'support-image-preview-backdrop';
const parseSsePayload = (rawData) => {
  try {
    return JSON.parse(rawData || '{}');
  } catch (_error) {
    return {};
  }
};

const SupportRequestWidget = ({ token, buttonClassName = '', fixed = true }) => {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [chat, setChat] = useState(null);
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [error, setError] = useState('');
  const [textError, setTextError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [viewportHeight, setViewportHeight] = useState(null);
  const [hasUnreadSupportMessage, setHasUnreadSupportMessage] = useState(false);
  const fileInputRef = useRef(null);
  const chatLogRef = useRef(null);
  const chatBottomRef = useRef(null);

  const appendIncomingMessage = (incomingMessage) => {
    if (!incomingMessage?.id) {
      return;
    }
    setChat((prev) => {
      const previousMessages = Array.isArray(prev?.messages) ? prev.messages : [];
      if (previousMessages.some((item) => item.id === incomingMessage.id)) {
        return prev;
      }
      return {
        ...(prev || {}),
        messages: [...previousMessages, incomingMessage],
      };
    });
  };

  const nextSlotIndex = attachments.length;
  const canAttachMore = attachments.length < MAX_FILES;

  useEffect(() => () => {
    attachments.forEach((attachment) => URL.revokeObjectURL(attachment.previewUrl));
  }, [attachments]);

  const chatMessages = useMemo(() => (Array.isArray(chat?.messages) ? chat.messages : []), [chat]);
  const conductorMessagesInRow = useMemo(() => {
    if (!chatMessages.length) {
      return 0;
    }

    let count = 0;
    for (let index = chatMessages.length - 1; index >= 0; index -= 1) {
      const item = chatMessages[index];
      if (item?.senderType === 'conductor') {
        count += 1;
        continue;
      }
      break;
    }

    return count;
  }, [chatMessages]);

  const resetComposer = () => {
    attachments.forEach((attachment) => URL.revokeObjectURL(attachment.previewUrl));
    setAttachments([]);
    setMessage('');
    setError('');
    setTextError('');
  };

  const loadChat = async () => {
    if (!token) {
      return;
    }
    setIsLoadingChat(true);
    setError('');
    try {
      const response = await fetch(apiUrl('/api/support-tickets/current'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(t('support.errors.loadFailed'));
      }
      const payload = await response.json();
      setChat(payload?.chat || null);
    } catch (loadError) {
      setError(loadError?.message || t('support.errors.loadFailed'));
    } finally {
      setIsLoadingChat(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadChat();
    }
  }, [isOpen]);

  const scrollChatToBottom = (behavior = 'smooth') => {
    const chatNode = chatLogRef.current;
    if (!chatNode) {
      return;
    }

    const isInstant = behavior === 'auto';
    if (chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({
        block: 'end',
        behavior: isInstant ? 'auto' : 'smooth',
      });
      return;
    }

    chatNode.scrollTo({
      top: chatNode.scrollHeight,
      behavior: isInstant ? 'auto' : 'smooth',
    });
  };

  useEffect(() => {
    if (!token || typeof window === 'undefined' || typeof EventSource === 'undefined') {
      return undefined;
    }

    const eventStream = new EventSource(apiUrl(`/api/support-tickets/events?token=${encodeURIComponent(token)}`));
    const handleIncomingMessage = (event) => {
      const payload = parseSsePayload(event?.data);
      if (payload?.message) {
        appendIncomingMessage(payload.message);
        const isIncomingSupportMessage = payload.message.senderType === 'admin';
        if (isIncomingSupportMessage) {
          if (isOpen) {
            requestAnimationFrame(() => scrollChatToBottom('smooth'));
          } else {
            setHasUnreadSupportMessage(true);
          }
        }
      }
      if (payload?.status) {
        setChat((prev) => ({ ...(prev || {}), status: payload.status }));
      }
    };
    const handleStatusChange = (event) => {
      const payload = parseSsePayload(event?.data);
      if (payload?.status) {
        setChat((prev) => ({ ...(prev || {}), status: payload.status }));
      }
    };

    eventStream.addEventListener('support_message_created', handleIncomingMessage);
    eventStream.addEventListener('support_ticket_status_updated', handleStatusChange);

    return () => {
      eventStream.removeEventListener('support_message_created', handleIncomingMessage);
      eventStream.removeEventListener('support_ticket_status_updated', handleStatusChange);
      eventStream.close();
    };
  }, [isOpen, token]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setHasUnreadSupportMessage(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || isLoadingChat) {
      return;
    }
    const timeoutId = setTimeout(() => {
      scrollChatToBottom('auto');
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [isOpen, isLoadingChat]);

  useEffect(() => {
    if (!isOpen || isLoadingChat) {
      return;
    }
    requestAnimationFrame(() => scrollChatToBottom('smooth'));
  }, [chatMessages.length, isOpen, isLoadingChat]);

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') {
      return undefined;
    }

    const updateViewportHeight = () => {
      const visualViewportHeight = window.visualViewport?.height;
      const nextHeight = Number.isFinite(visualViewportHeight)
        ? Math.round(visualViewportHeight)
        : window.innerHeight;
      setViewportHeight(nextHeight > 0 ? nextHeight : null);
    };

    updateViewportHeight();
    window.addEventListener('resize', updateViewportHeight);
    window.visualViewport?.addEventListener('resize', updateViewportHeight);
    window.visualViewport?.addEventListener('scroll', updateViewportHeight);

    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.visualViewport?.removeEventListener('resize', updateViewportHeight);
      window.visualViewport?.removeEventListener('scroll', updateViewportHeight);
    };
  }, [isOpen]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    if (!isOpen) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    const previousTouchAction = document.body.style.touchAction;

    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.touchAction = previousTouchAction;
    };
  }, [isOpen]);

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    setIsOpen(false);
    setPreviewImage('');
    resetComposer();
  };

  const validateFile = (file) => {
    if (!ACCEPTED_MIME_TYPES.includes(file.type.toLowerCase())) {
      return t('support.errors.invalidFormat');
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      return t('support.errors.fileTooLarge');
    }

    if (attachments.length >= MAX_FILES) {
      return t('support.errors.maxFiles');
    }

    return '';
  };

  const handleAddFileClick = () => {
    if (!canAttachMore || isSubmitting) {
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event) => {
    const [file] = event.target.files || [];
    event.target.value = '';

    if (!file) {
      return;
    }

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    const previewUrl = URL.createObjectURL(file);
    setAttachments((current) => [...current, { id: `${Date.now()}-${file.name}`, file, previewUrl }]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!token || isSubmitting) {
      return;
    }

    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      setTextError(t('support.errors.required'));
      return;
    }
    if (!isTextWithinLimit(trimmedMessage, TEXT_LIMITS.supportText)) {
      setTextError(t('support.errors.tooLong', { count: TEXT_LIMITS.supportText }));
      return;
    }
    if (conductorMessagesInRow >= MAX_CONDUCTOR_MESSAGES_IN_ROW) {
      setError(t('support.errors.messageLimitReached'));
      return;
    }

    setIsSubmitting(true);
    setError('');
    setTextError('');

    try {
      const formData = new FormData();
      formData.append('text', trimmedMessage);
      attachments.forEach((attachment) => formData.append('attachments', attachment.file));

      const response = await fetchWithIdempotencyRetry(apiUrl('/api/support-tickets'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const serverCode = payload?.error;
        if (serverCode === 'INVALID_IMAGE') {
          throw new Error(t('support.errors.invalidFormat'));
        }
        if (serverCode === 'FILE_TOO_LARGE') {
          throw new Error(t('support.errors.fileTooLarge'));
        }
        if (serverCode === 'TOO_MANY_FILES') {
          throw new Error(t('support.errors.maxFiles'));
        }
        if (serverCode === 'FIELD_TOO_LONG' && payload?.details?.field === 'text') {
          throw new Error(t('support.errors.tooLong', { count: TEXT_LIMITS.supportText }));
        }
        if (serverCode === 'SUPPORT_MESSAGE_LIMIT_REACHED') {
          throw new Error(t('support.errors.messageLimitReached'));
        }
        throw new Error(t('support.errors.submitFailed'));
      }

      const payload = await response.json().catch(() => null);
      if (payload?.chat) {
        setChat((prev) => ({
          ...(prev || {}),
          ...payload.chat,
          messages: Array.isArray(prev?.messages) ? prev.messages : [],
        }));
        if (payload?.message) {
          appendIncomingMessage(payload.message);
        }
      } else if (payload?.message) {
        appendIncomingMessage(payload.message);
      }
      resetComposer();
    } catch (submitError) {
      setError(submitError?.message || t('support.errors.submitFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const resolveSenderLabel = (senderType) => {
    if (senderType === 'admin') {
      return t('support.chat.supportLabel');
    }
    return t('support.chat.youLabel');
  };

  return (
    <>
      {!isOpen && (
        <button
          type="button"
          className={`support-widget-button${fixed ? '' : ' support-widget-button--inline'}${buttonClassName ? ` ${buttonClassName}` : ''}`}
          onClick={() => setIsOpen(true)}
          aria-label={t('support.button')}
        >
          {hasUnreadSupportMessage && <span className="support-widget-button__indicator" aria-hidden="true" />}
          <span aria-hidden="true">📣</span>
          <span>{t('support.button')}</span>
        </button>
      )}

      {isOpen && (
        <div className="modal-backdrop support-chat-backdrop" role="dialog" aria-modal="true" aria-label={t('support.chat.title')}>
          <form
            className="modal support-modal support-modal--chat"
            onSubmit={handleSubmit}
            style={viewportHeight ? { '--support-chat-viewport-height': `${viewportHeight}px` } : undefined}
          >
            <div className="modal-header">
              <h2>{t('support.chat.title')}</h2>
              <button
                type="button"
                className="support-modal__close"
                onClick={handleClose}
                aria-label={t('support.chat.closeAria')}
                disabled={isSubmitting}
              >
                ×
              </button>
            </div>

            <div className="support-chat-log" ref={chatLogRef}>
              {isLoadingChat && <p className="muted">{t('support.chat.loading')}</p>}
              {!isLoadingChat && chatMessages.length === 0 && (
                <p className="muted">{t('support.chat.empty')}</p>
              )}
              {!isLoadingChat && chatMessages.map((item) => {
                const isMine = item.senderType === 'conductor';
                return (
                  <div key={item.id} className={`support-chat-message${isMine ? ' support-chat-message--mine' : ''}`}>
                    <div className="support-chat-message__meta">{resolveSenderLabel(item.senderType)}</div>
                    <p>{item.text}</p>
                    {Array.isArray(item.attachments) && item.attachments.length > 0 && (
                      <div className="support-chat-message__images">
                        {item.attachments.map((attachment, index) => (
                          <button
                            key={`${item.id}-${attachment}-${index}`}
                            type="button"
                            className="support-chat-message__thumb"
                            onClick={() => setPreviewImage(apiUrl(attachment))}
                          >
                            <img src={apiUrl(attachment)} alt={t('support.previewAlt', { index: index + 1 })} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={chatBottomRef} className="support-chat-log__bottom-anchor" />
            </div>

            <div className="support-modal__composer">
              <textarea
                className={`support-modal__textarea${textError ? ' support-modal__textarea--error' : ''}`}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder={t('support.chat.placeholder')}
                rows={2}
                maxLength={TEXT_LIMITS.supportText}
                disabled={isSubmitting}
              />
              {canAttachMore && (
                <button
                  type="button"
                  className="support-modal__attach-button"
                  onClick={handleAddFileClick}
                  disabled={isSubmitting}
                  aria-label={t('support.uploadAria', { index: nextSlotIndex + 1 })}
                  title={t('support.chat.attachFile')}
                >
                  📎
                </button>
              )}
            </div>

            {textError && <p className="form-error">{textError}</p>}

            <div className="support-modal__files">
              {attachments.map((attachment, index) => (
                <div key={attachment.id} className="support-modal__thumb-wrap">
                  <img src={attachment.previewUrl} alt={t('support.previewAlt', { index: index + 1 })} className="support-modal__thumb" />
                </div>
              ))}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleFileSelect}
                hidden
              />
            </div>

            {error && <p className="form-error">{error}</p>}

            <div className="modal-actions">
              <button
                type="button"
                className="secondary-button support-modal__cancel-button"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t('support.cancel')}
              </button>
              <button type="submit" className="primary-button" disabled={isSubmitting}>
                {isSubmitting ? t('support.submitting') : t('support.chat.send')}
              </button>
            </div>
          </form>
        </div>
      )}

      {previewImage && (
        <div className={`modal-backdrop ${SUPPORT_PREVIEW_OVERLAY_CLASS}`} role="dialog" aria-modal="true" onClick={() => setPreviewImage('')}>
          <div className="support-image-preview" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="support-image-preview__close" onClick={() => setPreviewImage('')} aria-label={t('support.chat.closeAria')}>
              ×
            </button>
            <img src={previewImage} alt={t('support.chat.previewOriginalAlt')} />
          </div>
        </div>
      )}
    </>
  );
};

export default SupportRequestWidget;
